// ============================================
// PlayoNews Point System
// ============================================

export const POINTS = {
  LIKE: 5,
  SHARE: 15,
  CAPTION: 25,
  COMMENT: 10,
  DAILY_LOGIN: 50,
  STREAK_BONUS: 100,
  REFERRAL: 500,
} as const;

// ============================================
// Tier System
// ============================================

export interface Tier {
  name: string;
  threshold: number;
  color: string;
  gradient: string;
  icon: string;
}

export const TIERS: Tier[] = [
  {
    name: 'Bronze',
    threshold: 0,
    color: '#CD7F32',
    gradient: 'linear-gradient(135deg, #CD7F32, #A0522D)',
    icon: '🥉',
  },
  {
    name: 'Silver',
    threshold: 1_000_000,
    color: '#C0C0C0',
    gradient: 'linear-gradient(135deg, #C0C0C0, #A8A8A8)',
    icon: '🥈',
  },
  {
    name: 'Gold',
    threshold: 5_000_000,
    color: '#FFD700',
    gradient: 'linear-gradient(135deg, #FFD700, #FFA500)',
    icon: '🥇',
  },
  {
    name: 'Platinum',
    threshold: 10_000_000,
    color: '#E5E4E2',
    gradient: 'linear-gradient(135deg, #E5E4E2, #BCC6CC)',
    icon: '💎',
  },
  {
    name: 'Diamond',
    threshold: 25_000_000,
    color: '#B9F2FF',
    gradient: 'linear-gradient(135deg, #B9F2FF, #7DF9FF)',
    icon: '💠',
  },
  {
    name: 'Master',
    threshold: 50_000_000,
    color: '#9B59B6',
    gradient: 'linear-gradient(135deg, #9B59B6, #8E44AD)',
    icon: '👑',
  },
  {
    name: 'Legend',
    threshold: 100_000_000,
    color: '#FF4500',
    gradient: 'linear-gradient(135deg, #FF4500, #FF6347, #FFD700)',
    icon: '🏆',
  },
] as const;

export const TIER_THRESHOLDS = {
  Bronze: 0,
  Silver: 1_000_000,
  Gold: 5_000_000,
  Platinum: 10_000_000,
  Diamond: 25_000_000,
  Master: 50_000_000,
  Legend: 100_000_000,
} as const;

export const TIER_COLORS = {
  Bronze: '#CD7F32',
  Silver: '#C0C0C0',
  Gold: '#FFD700',
  Platinum: '#E5E4E2',
  Diamond: '#B9F2FF',
  Master: '#9B59B6',
  Legend: '#FF4500',
} as const;

export type TierName = keyof typeof TIER_THRESHOLDS;

/**
 * Get the tier for a given point total.
 */
export function getTierForPoints(points: number): Tier {
  for (let i = TIERS.length - 1; i >= 0; i--) {
    if (points >= TIERS[i].threshold) {
      return TIERS[i];
    }
  }
  return TIERS[0];
}

/**
 * Get progress to the next tier (0–1).
 */
export function getTierProgress(points: number): number {
  const currentTier = getTierForPoints(points);
  const currentIndex = TIERS.findIndex((t) => t.name === currentTier.name);
  if (currentIndex === TIERS.length - 1) return 1;
  const nextTier = TIERS[currentIndex + 1];
  const range = nextTier.threshold - currentTier.threshold;
  const progress = (points - currentTier.threshold) / range;
  return Math.min(Math.max(progress, 0), 1);
}

// ============================================
// Ad Position Types
// ============================================

export const AD_POSITIONS = {
  FEED_INLINE: 'feed_inline',
  SIDEBAR: 'sidebar',
  BANNER_TOP: 'banner_top',
  BANNER_BOTTOM: 'banner_bottom',
  INTERSTITIAL: 'interstitial',
  ARTICLE_INLINE: 'article_inline',
  NATIVE_CARD: 'native_card',
} as const;

export type AdPosition = (typeof AD_POSITIONS)[keyof typeof AD_POSITIONS];

// Frequency: show an ad every N items in the feed
export const AD_FEED_FREQUENCY = 5;

// ============================================
// News Categories
// ============================================

export const NEWS_CATEGORIES = [
  'trending',
  'politics',
  'technology',
  'sports',
  'entertainment',
  'science',
  'health',
  'business',
  'world',
] as const;

export type NewsCategory = (typeof NEWS_CATEGORIES)[number];

// ============================================
// API Configuration
// ============================================

export const API_CONFIG = {
  SQUIBLA_BASE_URL: import.meta.env.VITE_SQUIBLA_API_URL || 'http://localhost:8095',
  GIPHY_API_KEY: import.meta.env.VITE_GIPHY_API_KEY || '',
  GOOGLE_SEARCH_API_KEY: import.meta.env.VITE_GOOGLE_SEARCH_API_KEY || '',
  GOOGLE_SEARCH_CX: import.meta.env.VITE_GOOGLE_SEARCH_CX || '',
} as const;

// ============================================
// Misc Constants
// ============================================

export const MAX_CAPTION_LENGTH = 280;
export const MAX_COMMENT_LENGTH = 1000;
export const FEED_PAGE_SIZE = 20;
export const SEARCH_DEBOUNCE_MS = 300;
